import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';

const MainLayout = lazy(() => import('./components/layout/MainLayout'));
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const SupermarketsPage = lazy(() => import('./pages/SupermarketsPage'));
const SupermarketPage = lazy(() => import('./pages/SupermarketPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));

const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const PaymentVerifyPage = lazy(() => import('./pages/PaymentVerifyPage'));
const FileDisputePage = lazy(() => import('./pages/FileDisputePage'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1565c0', light: '#5e92f3', dark: '#003c8f' },
    secondary: { main: '#e65100' },
    background: { default: '#f8f9fa', paper: '#ffffff' },
  },
  typography: { fontFamily: '"Inter", "Roboto", sans-serif' },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
    MuiCard: { styleOverrides: { root: { boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 12 } } },
  },
});

const LoadingScreen = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
    <CircularProgress size={48} color="primary" />
  </Box>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<HomePage />} />
          <Route path="supermarkets" element={<SupermarketsPage />} />
          <Route path="supermarket/:id" element={<SupermarketPage />} />
          <Route path="products/:id" element={<ProductPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
          <Route path="orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
          <Route path="orders/:id" element={<PrivateRoute><OrderTrackingPage /></PrivateRoute>} />
          <Route path="orders/:id/track" element={<PrivateRoute><OrderTrackingPage /></PrivateRoute>} />
          <Route path="track/:id" element={<PrivateRoute><OrderTrackingPage /></PrivateRoute>} />
          <Route path="tracking/:id" element={<PrivateRoute><OrderTrackingPage /></PrivateRoute>} />
          <Route path="live-tracking/:id" element={<PrivateRoute><OrderTrackingPage /></PrivateRoute>} />
          <Route path="orders/:id/dispute" element={<PrivateRoute><FileDisputePage /></PrivateRoute>} />
          <Route path="profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="favorites" element={<PrivateRoute><FavoritesPage /></PrivateRoute>} />
          <Route path="contact" element={<ContactUsPage />} />
          <Route path="partner" element={<PartnerPage />} />
          <Route path="payment/verify" element={<PrivateRoute><PaymentVerifyPage /></PrivateRoute>} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <ToastContainer position="bottom-right" autoClose={3500} />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
